/**
 * Shopping Cart Service
 *
 * Manages shopping cart state with localStorage persistence.
 * Handles cart operations: add, remove, update, clear.
 * Calculates totals and validates items.
 */

const CART_STORAGE_KEY = 'ce_shopping_cart';
const CART_VERSION = '1.0';

/**
 * Cart Item Structure:
 * {
 *   listingId: string,
 *   title: string,
 *   price: number,
 *   quantity: number,
 *   size: string (e.g., "MICRO", "SMALL", "MEDIUM", "LARGE"),
 *   color: string (optional - for stickers with color variations),
 *   imageUrl: string,
 *   etsyUrl: string,
 *   sizeVariationId: number (optional - for Etsy deeplinks),
 *   colorVariationId: number (optional - for Etsy deeplinks),
 *   isSubscriberOnly: boolean,
 *   isEarlyAccess: boolean
 * }
 */

class CartService {
  constructor() {
    this.listeners = [];
    this.cart = this.loadCart();
  }

  /**
   * Load cart from localStorage
   */
  loadCart() {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (!stored) {
        return this.createEmptyCart();
      }

      const parsed = JSON.parse(stored);

      // Version check - clear cart if version mismatch
      if (parsed.version !== CART_VERSION) {
        console.log('Cart version mismatch, creating new cart');
        return this.createEmptyCart();
      }

      // Validate cart structure
      if (!parsed.items || !Array.isArray(parsed.items)) {
        return this.createEmptyCart();
      }

      return parsed;
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return this.createEmptyCart();
    }
  }

  /**
   * Create empty cart structure
   */
  createEmptyCart() {
    return {
      version: CART_VERSION,
      items: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Save cart to localStorage
   */
  saveCart() {
    try {
      this.cart.updatedAt = new Date().toISOString();
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(this.cart));
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }

  /**
   * Add item to cart
   * If item already exists with same size/color, increment quantity
   */
  addItem(item) {
    // Validate required fields
    if (!item.listingId || !item.title || !item.price || !item.size) {
      console.error('Invalid item - missing required fields:', item);
      return { success: false, error: 'Invalid item data' };
    }

    // Create unique key for item (listingId + size + color)
    const itemKey = this.getItemKey(item);

    // Check if item already exists
    const existingIndex = this.cart.items.findIndex(
      (cartItem) => this.getItemKey(cartItem) === itemKey
    );

    if (existingIndex >= 0) {
      // Item exists - increment quantity
      this.cart.items[existingIndex].quantity += item.quantity || 1;
      console.log(
        `📦 Updated cart item quantity: ${this.cart.items[existingIndex].title} (${this.cart.items[existingIndex].quantity})`
      );
    } else {
      // New item - add to cart
      const newItem = {
        ...item,
        quantity: item.quantity || 1,
        addedAt: new Date().toISOString(),
      };
      this.cart.items.push(newItem);
      console.log(`📦 Added to cart: ${newItem.title} (${newItem.size})`);
    }

    this.saveCart();
    return { success: true, itemCount: this.getItemCount() };
  }

  /**
   * Remove item from cart
   */
  removeItem(listingId, size, color = null) {
    const itemKey = this.getItemKey({ listingId, size, color });
    const initialLength = this.cart.items.length;

    this.cart.items = this.cart.items.filter((item) => this.getItemKey(item) !== itemKey);

    const removed = initialLength !== this.cart.items.length;
    if (removed) {
      console.log(`🗑️ Removed from cart: ${listingId} (${size})`);
      this.saveCart();
    }

    return { success: removed, itemCount: this.getItemCount() };
  }

  /**
   * Update item quantity
   */
  updateQuantity(listingId, size, color = null, quantity) {
    if (quantity < 1) {
      return this.removeItem(listingId, size, color);
    }

    const itemKey = this.getItemKey({ listingId, size, color });
    const item = this.cart.items.find((cartItem) => this.getItemKey(cartItem) === itemKey);

    if (!item) {
      return { success: false, error: 'Item not found in cart' };
    }

    item.quantity = quantity;
    console.log(`📝 Updated quantity: ${item.title} = ${quantity}`);
    this.saveCart();

    return { success: true, itemCount: this.getItemCount() };
  }

  /**
   * Clear entire cart
   */
  clearCart() {
    const hadItems = this.cart.items.length > 0;
    this.cart = this.createEmptyCart();
    this.saveCart();

    if (hadItems) {
      console.log('🧹 Cart cleared');
    }

    return { success: true, itemCount: 0 };
  }

  /**
   * Get all cart items
   */
  getItems() {
    return [...this.cart.items];
  }

  /**
   * Get specific item from cart
   */
  getItem(listingId, size, color = null) {
    const itemKey = this.getItemKey({ listingId, size, color });
    return this.cart.items.find((item) => this.getItemKey(item) === itemKey);
  }

  /**
   * Get total number of items (sum of quantities)
   */
  getItemCount() {
    return this.cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Get number of unique items (not quantity)
   */
  getUniqueItemCount() {
    return this.cart.items.length;
  }

  /**
   * Calculate cart subtotal (before shipping, tax, etc.)
   */
  getSubtotal() {
    return this.cart.items.reduce((total, item) => {
      return total + item.price * item.quantity;
    }, 0);
  }

  /**
   * Calculate estimated shipping
   * TODO: Replace with actual shipping calculation or Stripe shipping rates
   */
  getEstimatedShipping() {
    // Shipping will be calculated by Stripe or configured separately
    return 0;
  }

  /**
   * Calculate cart total (subtotal + shipping)
   */
  getTotal() {
    return this.getSubtotal() + this.getEstimatedShipping();
  }

  /**
   * Check if specific item is in cart
   */
  isInCart(listingId, size, color = null) {
    const itemKey = this.getItemKey({ listingId, size, color });
    return this.cart.items.some((item) => this.getItemKey(item) === itemKey);
  }

  /**
   * Get quantity of specific item in cart
   */
  getItemQuantity(listingId, size, color = null) {
    const item = this.getItem(listingId, size, color);
    return item ? item.quantity : 0;
  }

  /**
   * Create unique key for cart item
   */
  getItemKey(item) {
    const colorPart = item.color ? `-${item.color}` : '';
    return `${item.listingId}-${item.size}${colorPart}`;
  }

  /**
   * Validate cart items (check for subscriber-only items, etc.)
   * Returns array of validation errors
   */
  validateCart(isSubscriber = false) {
    const errors = [];

    this.cart.items.forEach((item) => {
      // Check subscriber-only items
      if (item.isSubscriberOnly && !isSubscriber) {
        errors.push({
          listingId: item.listingId,
          size: item.size,
          color: item.color,
          error: 'Subscriber-only item - subscription required',
          title: item.title,
        });
      }

      // Check early access items
      if (item.isEarlyAccess && !isSubscriber) {
        errors.push({
          listingId: item.listingId,
          size: item.size,
          color: item.color,
          error: 'Early access item - subscription required',
          title: item.title,
        });
      }

      // Check price validity
      if (!item.price || item.price <= 0) {
        errors.push({
          listingId: item.listingId,
          size: item.size,
          color: item.color,
          error: 'Invalid price',
          title: item.title,
        });
      }

      // Check quantity validity
      if (!item.quantity || item.quantity < 1) {
        errors.push({
          listingId: item.listingId,
          size: item.size,
          color: item.color,
          error: 'Invalid quantity',
          title: item.title,
        });
      }
    });

    return errors;
  }

  /**
   * Add listener for cart changes
   */
  addListener(callback) {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  /**
   * Notify all listeners of cart changes
   */
  notifyListeners() {
    const cartData = {
      items: this.getItems(),
      itemCount: this.getItemCount(),
      uniqueItemCount: this.getUniqueItemCount(),
      subtotal: this.getSubtotal(),
      shipping: this.getEstimatedShipping(),
      total: this.getTotal(),
    };

    this.listeners.forEach((callback) => {
      try {
        callback(cartData);
      } catch (error) {
        console.error('Error in cart listener:', error);
      }
    });
  }

  /**
   * Export cart data for checkout
   */
  exportForCheckout() {
    return {
      items: this.cart.items.map((item) => ({
        listingId: item.listingId,
        name: item.title,
        price: Math.round(item.price * 100), // Convert dollars to cents for Stripe
        quantity: item.quantity,
        size: item.size,
        color: item.color,
        image: item.imageUrl,
        sizeVariationId: item.sizeVariationId,
        colorVariationId: item.colorVariationId,
      })),
      subtotal: this.getSubtotal(),
      shipping: this.getEstimatedShipping(),
      total: this.getTotal(),
    };
  }

  /**
   * Get cart summary for display
   */
  getSummary() {
    return {
      itemCount: this.getItemCount(),
      uniqueItemCount: this.getUniqueItemCount(),
      subtotal: this.getSubtotal(),
      shipping: this.getEstimatedShipping(),
      total: this.getTotal(),
      isEmpty: this.cart.items.length === 0,
    };
  }
}

// Create singleton instance
const cartService = new CartService();

export default cartService;
